import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabaseClient";
import { daysUntil, authedFetch } from "../lib/api";
import { TopReferrers } from "../components/TopReferrers";
import { DemoVideo } from "../components/DemoVideo";
import { LaunchStatusBanner } from "../components/LaunchStatusBanner";
import { ClinicianInsights } from "../components/ClinicianInsights";
import { AppHeader } from "../components/AppHeader";
import { Button } from "../components/ui/button";
import {
  LogOut, BadgeCheck, Building2, Users, KeyRound, MapPin, CalendarClock, Loader2, CreditCard,
  AlertTriangle, Plus, ArrowUpCircle, Gift, Clock,
} from "lucide-react";
import { SRMark } from "../components/SRMark";
import { Input } from "../components/ui/input";
import { SpecialtyMultiSelect, SpecialtyChips, LanguageMultiSelect, LanguageChips } from "../components/SpecialtyMultiSelect";
import { SuburbAutocomplete } from "../components/SuburbAutocomplete";
import { PhotoUpload } from "../components/PhotoUpload";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { AppointmentTypeSelect } from "../components/AppointmentTypeSelect";
import { normaliseExpertiseList } from "../lib/clinicalOptions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// How many clinicians each plan tier allows.
const TIER_LIMITS = { SOLO: 1, CLINIC_LIGHT: 5, CLINIC_PLUS: Infinity };
const tierLimit = (tier) => TIER_LIMITS[tier] ?? 1;
const limitLabel = (n) => (n === Infinity ? "unlimited" : String(n));
const fmtTrialDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" }) : "—";

import { AVAIL_OPTIONS } from "../lib/capacity";
const AVAIL = Object.fromEntries(AVAIL_OPTIONS.map((o) => [o.value, { label: o.label, style: o.style }]));
const availMeta = (v) => AVAIL[v] || { label: v || "—", style: { backgroundColor: "#F1F5F9", color: "#475569", borderColor: "#E2E8F0" } };

const fmtDateTime = (iso) =>
  iso ? new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

const STATUS_LABEL = {
  TRIAL: { label: "Trial", tone: "bg-amber-50 text-amber-700 border-amber-200" },
  ACTIVE: { label: "Active", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  PAST_DUE: { label: "Past due", tone: "bg-rose-50 text-rose-700 border-rose-200" },
  CANCELLED: { label: "Cancelled", tone: "bg-slate-100 text-slate-600 border-slate-200" },
  PAUSED: { label: "Paused", tone: "bg-violet-50 text-violet-700 border-violet-200" },
  FREE: { label: "Complimentary", tone: "bg-sky-50 text-sky-700 border-sky-200" },
  FREE_LIFETIME: { label: "Free for lifetime", tone: "bg-indigo-50 text-indigo-700 border-indigo-200" },
};

export default function ClinicAdminPortal() {
  const { user, loading: authLoading, signOut } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = ["overview", "team", "referrals", "insights"].includes(searchParams.get("tab")) ? searchParams.get("tab") : "overview";
  const [account, setAccount] = useState(null);
  const [clinicians, setClinicians] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notAdmin, setNotAdmin] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [insights, setInsights] = useState(null);

  const openBillingPortal = async () => {
    if (!account) return;
    setPortalLoading(true);
    try {
      const res = await authedFetch("/billing/portal", {
        method: "POST",
        body: JSON.stringify({ account_id: account.id, return_url: `${window.location.origin}/clinic` }),
      });
      const data = await res.json();
      if (res.ok && data.url) {
        window.location.href = data.url;
      } else {
        toast.error(data.detail || "Could not open the billing portal.");
        setPortalLoading(false);
      }
    } catch (e) {
      toast.error("Could not open the billing portal.");
      setPortalLoading(false);
    }
  };
  const [showAdd, setShowAdd] = useState(false);
  const [savingPref, setSavingPref] = useState(false);
  const [adding, setAdding] = useState(false);
  const [membershipInvites, setMembershipInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [invitingExisting, setInvitingExisting] = useState(false);
  const [savingAvailability, setSavingAvailability] = useState(null);
  const [emailDrafts, setEmailDrafts] = useState({});
  const [savingEmail, setSavingEmail] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", profession: "", specialty: "", appointment_types: [], practice_address: "", suburb: "", state: "VIC", postcode: "", email: "", ahpra_number: "", gender: "", languages: "English", photo_url: "" });

  const loadAccount = async () => {
    const { data: acct } = await supabase
      .from("accounts").select("*").eq("billing_email", user.email).maybeSingle();
    return acct;
  };
  const loadClinicians = async (accountId) => {
    const { data } = await supabase.from("clinicians").select("*").eq("account_id", accountId).order("last_name");
    setClinicians(data || []);
  };

  useEffect(() => {
    if (!user) return;
    let active = true;
    const load = async () => {
      let acct = await loadAccount();
      if (!active) return;
      const md = user.user_metadata || {};
      if (!acct && md.role === "clinic" && md.clinic_name) {
        await authedFetch("/onboard/clinic", { method: "POST", body: JSON.stringify({ account_name: md.clinic_name }) });
        acct = await loadAccount();
      }
      if (!active) return;
      if (!acct) { setNotAdmin(true); setLoading(false); return; }
      setAccount(acct);
      const [clinRes, tokRes, insightsRes, inviteRes] = await Promise.all([
        supabase.from("clinicians").select("*").eq("account_id", acct.id).order("last_name"),
        supabase.from("booking_tokens")
          .select("id,token,gp_clinic_name,created_at,clinician_id,clinicians!clinician_id(first_name,last_name)")
          .order("created_at", { ascending: false }),
        authedFetch("/insights/me"),
        authedFetch("/clinic/membership-invites"),
      ]);
      if (!active) return;
      setClinicians(clinRes.data || []);
      setTokens(tokRes.data || []);
      if (insightsRes.ok) setInsights(await insightsRes.json());
      if (inviteRes.ok) setMembershipInvites((await inviteRes.json()).sent || []);
      setLoading(false);
    };
    load();
    return () => { active = false; };
  }, [user]);

  const inviteExistingClinician = async (event) => {
    event.preventDefault();
    if (!inviteEmail.trim()) return;
    setInvitingExisting(true);
    const response = await authedFetch("/clinic/membership-invites", { method: "POST", body: JSON.stringify({ email: inviteEmail }) });
    const result = await response.json().catch(() => ({}));
    setInvitingExisting(false);
    if (!response.ok) return toast.error(result.detail || "Could not invite this clinician.");
    setInviteEmail("");
    setMembershipInvites((current) => [result, ...current]);
    toast.success("Invitation sent. The clinician must accept before their profile moves.");
  };

  // Handle return from Stripe Checkout (?session_id=...): poll status, then refresh.
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (searchParams.get("billing") === "cancelled") {
      toast.info("Checkout cancelled.");
      setSearchParams({}, { replace: true });
      return;
    }
    if (!sessionId || !user) return;
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await fetch(`${API}/billing/status/${sessionId}`);
        const data = await res.json();
        if (cancelled) return;
        if (data.payment_status === "paid") {
          toast.success("Subscription activated!");
          const acct = await loadAccount();
          if (acct) setAccount(acct);
          setSearchParams({}, { replace: true });
          return;
        }
        if (data.status === "expired" || attempts >= 6) {
          toast.error("Payment not completed.");
          setSearchParams({}, { replace: true });
          return;
        }
        setTimeout(poll, 1500);
      } catch (e) {
        if (attempts >= 6) { setSearchParams({}, { replace: true }); return; }
        setTimeout(poll, 1500);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [searchParams, user]);

  // Group referral events into threads (one per clinician), newest first.
  const referralThreads = useMemo(() => tokens, [tokens]);

  const handleLogout = async () => {
    await signOut();
    toast.success("Signed out.");
    navigate("/login", { replace: true });
  };

  const updatePromptSettings = async (updates) => {
    setSavingPref(true);
    const availability_prompt_recipient = updates.availability_prompt_recipient ?? account.availability_prompt_recipient ?? "BOTH";
    const availability_prompt_day = updates.availability_prompt_day ?? account.availability_prompt_day ?? 1;
    const res = await authedFetch("/clinic/settings", {
      method: "POST",
      body: JSON.stringify({ availability_prompt_recipient, availability_prompt_day }),
    });
    setSavingPref(false);
    if (!res.ok) { toast.error("Could not save preference."); return; }
    setAccount((a) => ({ ...a, availability_prompt_recipient, availability_prompt_day }));
    toast.success("Availability-prompt preference saved.");
  };

  const onTrial = account ? account.subscription_status === "TRIAL" : false;
  const trialHasStarted = Boolean(account?.trial_ends_at);
  const trialDaysLeft = account ? daysUntil(account.trial_ends_at) : null;
  const limit = account ? tierLimit(account.subscription_tier) : 1;
  const activeClinicianCount = clinicians.filter((clinician) => clinician.is_active !== false).length;
  const overLimit = account ? activeClinicianCount > limit : false;
  const atLimit = account ? activeClinicianCount >= limit : false;

  const updateClinicianAvailability = async (clinician, nextStatus) => {
    if (nextStatus === clinician.availability_status) return;
    const isVerified = clinician.verification_status === "VERIFIED" || clinician.is_verified === true;
    if (!isVerified) {
      toast.error("You won’t be able to change availability until this clinician’s AHPRA verification is complete.");
      return;
    }

    if (nextStatus === "CLOSED_BOOKS") {
      const anotherActiveClinicianIsOpen = clinicians.some(
        (item) => item.id !== clinician.id && item.is_active !== false && item.availability_status !== "CLOSED_BOOKS"
      );
      const message = anotherActiveClinicianIsOpen
        ? "Close this clinician’s books?\n\nThe clinic subscription remains active until every active clinician’s books are closed."
        : "Close this clinician’s books?\n\nThis is the last active clinician with open books, so the clinic’s monthly subscription will pause until any clinician reopens.";
      if (!window.confirm(message)) return;
    }

    const wasPaused = account?.billing_pause_state && account.billing_pause_state !== "ACTIVE";
    setSavingAvailability(clinician.id);
    try {
      const response = await authedFetch("/billing/availability", {
        method: "POST",
        body: JSON.stringify({ clinician_id: clinician.id, availability_status: nextStatus }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        toast.error(result.detail || "Could not update availability. Nothing was changed.");
        return;
      }
      setClinicians((current) => current.map((item) => (
        item.id === clinician.id ? { ...item, availability_status: result.availability_status } : item
      )));
      if (result.billing_pause_state) {
        setAccount((current) => current ? { ...current, billing_pause_state: result.billing_pause_state } : current);
      }
      if (result.billing_pause_state && result.billing_pause_state !== "ACTIVE") {
        toast.success("Availability updated. Every active clinician now has closed books, so the clinic subscription is paused until any clinician reopens.");
      } else if (wasPaused && result.billing_pause_state === "ACTIVE") {
        toast.success("Availability updated. The clinic subscription has resumed because a clinician reopened their books.");
      } else if (nextStatus === "CLOSED_BOOKS") {
        toast.success("Availability updated. The clinic subscription remains active until every active clinician’s books are closed.");
      } else {
        toast.success("Availability updated successfully.");
      }
    } catch {
      toast.error("Could not update availability. Nothing was changed.");
    } finally {
      setSavingAvailability(null);
    }
  };

  const updateClinicianEmail = async (clinician) => {
    const email = (emailDrafts[clinician.id] ?? clinician.email ?? "").trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Enter a valid clinician email address.");
      return;
    }
    if (email === (clinician.email || "").trim().toLowerCase()) return;

    setSavingEmail(clinician.id);
    try {
      const response = await authedFetch(`/clinic/clinicians/${clinician.id}/email`, {
        method: "PATCH",
        body: JSON.stringify({ email }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        toast.error(result.detail || "Could not update clinician email.");
        return;
      }
      setClinicians((current) => current.map((item) => (
        item.id === clinician.id ? { ...item, email: result.email } : item
      )));
      setEmailDrafts((current) => ({ ...current, [clinician.id]: result.email }));
      toast.success("Clinician contact email updated.");
    } catch {
      toast.error("Could not update clinician email.");
    } finally {
      setSavingEmail(null);
    }
  };

  const addClinician = async (e) => {
    e.preventDefault();
    if (atLimit) {
      toast.error(account.subscription_tier === "SOLO" ? "Upgrade to a GROUP clinic account to add a second clinician." : `Your ${account.subscription_tier} plan allows ${limitLabel(limit)} clinicians. Upgrade to add more.`);
      navigate("/clinic/subscribe");
      return;
    }
    if (!form.profession) {
      toast.error("Please select the clinician profession.");
      return;
    }
    if (!form.ahpra_number.trim()) {
      toast.error("Please enter the clinician's AHPRA registration number.");
      return;
    }
    if (!form.specialty.trim()) {
      toast.error("Please select at least one specialty.");
      return;
    }
    setAdding(true);
    const res = await authedFetch("/clinic/clinicians", {
      method: "POST",
      body: JSON.stringify({
        first_name: form.first_name,
        last_name: form.last_name,
        profession: form.profession,
        specialty: form.specialty,
        clinical_expertise: normaliseExpertiseList(form.specialty.split(",")),
        appointment_types: form.appointment_types,
        practice_address: form.practice_address,
        suburb: form.suburb,
        state: form.state,
        postcode: form.postcode,
        email: form.email,
        ahpra_number: form.ahpra_number.trim(),
        gender: form.gender,
        languages: form.languages,
        photo_url: form.photo_url,
      }),
    });
    setAdding(false);
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      if (d.detail?.code === "TIER_LIMIT_REACHED") {
        toast.error(d.detail.message);
        navigate("/clinic/subscribe");
      } else {
        toast.error("Could not add clinician: " + (typeof d.detail === "string" ? d.detail : "please try again."));
      }
      return;
    }
    toast.success("Clinician added — pending AHPRA verification.");
    setForm({ first_name: "", last_name: "", profession: "", specialty: "", appointment_types: [], practice_address: "", suburb: "", state: "VIC", postcode: "", email: "", ahpra_number: "", gender: "", languages: "English", photo_url: "" });
    setShowAdd(false);
    await loadClinicians(account.id);
  };
  const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  if (!authLoading && !user) return <Navigate to="/login" replace />;

  return (
    <div className="min-h-screen bg-background">
      <AppHeader firstName={user?.user_metadata?.first_name} role="clinic" />
      <LaunchStatusBanner audience="clinic" />

      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8 animate-fadein" data-testid="clinic-portal-page">
        {activeTab === "overview" && <div className="mb-4 flex justify-end"><DemoVideo src="/videos/clinic.mp4" title="How clinic managers use SimplyRefer" buttonLabel="How it works" variant="outline" size="sm" testId="clinic-demo-video" /></div>}
        {loading ? (
          <div className="flex items-center gap-2 text-slate-500" data-testid="clinic-portal-loading">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading clinic…
          </div>
        ) : notAdmin ? (
          <div className="rounded-md border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="clinic-portal-not-linked">
            <Building2 className="mx-auto h-8 w-8 text-slate-300" />
            <p className="mt-3 font-medium text-slate-700">No clinic account linked to this email</p>
            <p className="mt-1 text-sm text-slate-500">
              We couldn't find a clinic account for <span className="font-medium text-slate-700">{user?.email}</span>.
              If you haven't registered your clinic yet, create an account to get started.
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
              <Button asChild data-testid="clinic-portal-signup-button">
                <Link to="/clinic/signup">Register your clinic</Link>
              </Button>
              <Button variant="outline" onClick={handleLogout} data-testid="clinic-portal-signout-button">Sign out</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Account header + stats */}
            <section className={activeTab === "overview" ? "" : "hidden"}>
              <div className="flex items-center gap-3">
                <Building2 className="h-6 w-6 text-primary" />
                <h1 className="font-heading text-2xl font-semibold tracking-tight text-slate-900" data-testid="clinic-account-name">
                  {account.account_name}
                </h1>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 sm:max-w-2xl">
                <div className="rounded-md border border-slate-200 bg-white p-4">
                  <div className="flex items-center gap-2 text-slate-500"><Users className="h-4 w-4" /><span className="text-xs font-medium uppercase tracking-wide">Clinicians</span></div>
                  <p className="mt-1 font-heading text-2xl font-semibold text-slate-900" data-testid="clinic-clinician-count">{clinicians.length}</p>
                </div>
                <div className="rounded-md border border-slate-200 bg-white p-4">
                  <div className="flex items-center gap-2 text-slate-500"><KeyRound className="h-4 w-4" /><span className="text-xs font-medium uppercase tracking-wide">Selected referrals</span></div>
                  <p className="mt-1 font-heading text-2xl font-semibold text-slate-900" data-testid="clinic-referral-count">{insights?.totals?.selected_for_referral || 0}</p>
                </div>
                <div className="col-span-2 rounded-md border border-slate-200 bg-white p-4 sm:col-span-1">
                  <div className="flex items-center gap-2 text-slate-500"><CreditCard className="h-4 w-4" /><span className="text-xs font-medium uppercase tracking-wide">Plan</span></div>
                  <div className="mt-1 flex items-center gap-2">
                      <p className="font-heading text-2xl font-semibold text-slate-900" data-testid="clinic-plan-tier">
                        {account.subscription_status === "TRIAL" && !account.trial_ends_at
                          ? "Not selected"
                          : account.subscription_tier}
                      </p>
                    {(() => { const s = account.subscription_status === "TRIAL" && !account.trial_ends_at
                      ? { label: "Trial pending", tone: "bg-sky-50 text-sky-700 border-sky-200" }
                      : (STATUS_LABEL[account.subscription_status] || STATUS_LABEL.TRIAL); return (
                      <span className={`rounded-md border px-2 py-0.5 text-xs font-medium ${s.tone}`} data-testid="clinic-plan-status">{s.label}</span>
                    ); })()}
                  </div>
                  <Button asChild variant="outline" size="sm" className="mt-3 w-full" data-testid="manage-subscription-button">
                    <Link to="/clinic/subscribe">{onTrial ? "Choose a plan" : "Change plan"}</Link>
                  </Button>
                  <Button variant="ghost" size="sm" className="mt-2 w-full" onClick={openBillingPortal} disabled={portalLoading} data-testid="manage-billing-button">
                    {portalLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
                    Manage billing & payment
                  </Button>
                </div>
              </div>
            </section>

            <div className={activeTab === "insights" ? "" : "hidden"}><ClinicianInsights insights={insights} combined /></div>

            {/* Free-trial banner */}
            {onTrial && (
              trialHasStarted ? (
                <div className={`${activeTab === "overview" ? "" : "hidden"} rounded-md border border-emerald-200 bg-emerald-50 p-5`} data-testid="trial-banner">
                  <div className="flex items-start gap-3">
                    <Gift className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                    <div className="flex-1">
                      <p className="font-medium text-emerald-900">You're on your free trial</p>
                      <p className="mt-0.5 text-sm text-emerald-800">
                        {trialDaysLeft >= 0
                          ? <>Full access — add unlimited clinicians. <span className="font-semibold inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{trialDaysLeft} day{trialDaysLeft === 1 ? "" : "s"} left</span>. Choose a plan before it ends to stay listed.</>
                          : "Your free trial has ended — choose a plan to keep your clinicians listed."}
                      </p>
                      <p className="mt-1 text-xs text-emerald-700">{fmtTrialDate(account.trial_started_at)} to {fmtTrialDate(account.trial_ends_at)}</p>
                    </div>
                    <Button asChild size="sm" variant="outline" className="border-emerald-300 text-emerald-800 hover:bg-emerald-100" data-testid="trial-choose-plan-button">
                      <Link to="/clinic/subscribe">Choose a plan</Link>
                    </Button>
                  </div>
                </div>
              ) : (
                <div className={`${activeTab === "overview" ? "" : "hidden"} rounded-md border border-sky-200 bg-sky-50 p-5`} data-testid="trial-pending-banner">
                  <div className="flex items-start gap-3">
                    <Gift className="mt-0.5 h-5 w-5 shrink-0 text-sky-600" />
                    <div>
                      <p className="font-medium text-sky-900">Your free trial has not started yet</p>
                      <p className="mt-0.5 text-sm text-sky-800">Your full 90-day free trial begins only when SimplyRefer goes live. You can keep adding clinicians while AHPRA verification is pending; a plan and card are optional before launch.</p>
                    </div>
                  </div>
                </div>
              )
            )}

            {/* Plan-limit upgrade banner */}
            {overLimit && (
              <div className={`${activeTab === "overview" ? "" : "hidden"} rounded-md border border-amber-300 bg-amber-50 p-5`} data-testid="upgrade-banner">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                  <div className="flex-1">
                    <p className="font-medium text-amber-900">Upgrade required</p>
                    <p className="mt-0.5 text-sm text-amber-800">
                      Your <span className="font-semibold">{account.subscription_tier}</span> plan supports {limitLabel(limit)} clinician{limit === 1 ? "" : "s"}, but your clinic has <span className="font-semibold">{clinicians.length}</span>. Upgrade to a clinic plan to keep all clinicians active.
                    </p>
                  </div>
                  <Button asChild size="sm" data-testid="banner-upgrade-button">
                    <Link to="/clinic/subscribe"><ArrowUpCircle className="mr-2 h-4 w-4" /> Upgrade plan</Link>
                  </Button>
                </div>
              </div>
            )}

            {/* Monthly availability prompt settings */}
            <div className={`${activeTab === "team" ? "" : "hidden"} rounded-md border border-slate-200 bg-white p-5`} data-testid="availability-prompt-settings">
              <h3 className="font-heading text-sm font-semibold text-slate-900">Monthly availability prompt</h3>
              <p className="mt-0.5 text-xs text-slate-500">We ask your selected recipients to confirm each clinician's availability monthly. No response after two reminders sets the clinician to "Closed books".</p>
              {account.availability_prompt_recipient === "MANAGER" ? (
                <p className="mt-2 max-w-xl text-sm leading-relaxed text-slate-600">
                  You receive the email on behalf of your clinicians. It includes a secure one-click link to confirm each clinician's availability is still correct, plus a separate link to update it. Your clinicians do not need an account or login.
                </p>
              ) : account.availability_prompt_recipient === "BOTH" ? (
                <p className="mt-2 max-w-xl text-sm leading-relaxed text-slate-600">
                  You and your clinicians receive the email. Clinicians get a secure one-click confirmation link with no login required, plus a separate portal link to make changes. You can also confirm or update availability on their behalf.
                </p>
              ) : (
                <p className="mt-2 max-w-xl text-sm leading-relaxed text-slate-600">
                  Clinicians receive an email with a secure one-click link to confirm their availability is still correct — no account or login needed. A separate link lets them change it in their own portal.
                </p>
              )}
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <label className="text-xs font-medium text-slate-700">Recipients
                  <select value={account.availability_prompt_recipient || "BOTH"} onChange={(e) => updatePromptSettings({ availability_prompt_recipient: e.target.value })} disabled={savingPref} data-testid="availability-prompt-recipient-select" className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-700 focus:border-primary focus:outline-none">
                    <option value="BOTH">Send to clinic manager &amp; clinicians</option>
                    <option value="CLINICIANS">Send to clinicians only</option>
                    <option value="MANAGER">Send to me (clinic manager) only</option>
                  </select>
                </label>
                <label className="text-xs font-medium text-slate-700">Send on day of month
                  <select value={account.availability_prompt_day || 1} onChange={(e) => updatePromptSettings({ availability_prompt_day: Number(e.target.value) })} disabled={savingPref} data-testid="availability-prompt-day-select" className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-700 focus:border-primary focus:outline-none">
                    {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => <option key={day} value={day}>{day}</option>)}
                  </select>
                </label>
              </div>
              <p className="mt-2 text-xs text-slate-500">Defaults to the 1st. If that date falls on a weekend or a national public holiday, we'll send it on the closest business day before.</p>
            </div>
            <section className={activeTab === "team" ? "" : "hidden"} data-testid="clinic-clinicians-section">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-heading text-lg font-semibold text-slate-900">Your clinicians</h2>
                  <p className="mt-1 text-xs text-slate-600">Add a new profile for a clinician who does not have a SimplyRefer login.</p>
                  <p className="text-xs text-slate-500">
                    {onTrial
                      ? `${clinicians.length} clinician${clinicians.length === 1 ? "" : "s"} • Free trial (unlimited)`
                      : `${clinicians.length} of ${limitLabel(limit)} used on the ${account.subscription_tier} plan`}
                  </p>
                </div>
                <Button size="sm" variant="outline" onClick={() => (atLimit ? navigate("/clinic/subscribe") : setShowAdd((v) => !v))} data-testid="add-clinician-button">
                  <Plus className="mr-2 h-4 w-4" /> Add clinician
                </Button>
              </div>

              {showAdd && !atLimit && (
                <form onSubmit={addClinician} className="mt-4 rounded-md border border-slate-200 bg-white p-5" data-testid="add-clinician-form">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5"><Label>First name</Label><Input required value={form.first_name} onChange={setF("first_name")} data-testid="add-firstname" /></div>
                    <div className="space-y-1.5"><Label>Last name</Label><Input required value={form.last_name} onChange={setF("last_name")} data-testid="add-lastname" /></div>
                    <div className="space-y-1.5">
                <Label>Profession</Label>
                <Select value={form.profession} onValueChange={(value) => setForm((current) => ({ ...current, profession: value, specialty: "" }))}>
                  <SelectTrigger data-testid="add-profession">
                    <SelectValue placeholder="Select profession" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="Psychologist">Psychologist</SelectItem>
                    <SelectItem value="Psychiatrist">Psychiatrist</SelectItem>
                  </SelectContent>
                </Select>
              </div>
                    <div className="space-y-1.5">
                      <Label>Specialty</Label>
                      <SpecialtyMultiSelect value={form.specialty} profession={form.profession} onChange={(csv) => setForm((f) => ({ ...f, specialty: csv }))} testId="add-specialty" />
                      {form.specialty.trim() && (
                        <SpecialtyChips value={form.specialty} onRemove={(s) => setForm((f) => ({ ...f, specialty: f.specialty.split(",").map((x) => x.trim()).filter((x) => x && x !== s).join(", ") }))} />
                      )}
                    </div>
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label>Appointment type</Label>
                      <AppointmentTypeSelect value={form.appointment_types} onChange={(value) => setForm((current) => ({ ...current, appointment_types: value }))} testId="add-appointment-types" />
                      <p className="text-xs text-slate-500">Choose either or both. This is optional and only helps GPs narrow their search.</p>
                    </div>
                    <div className="space-y-1.5"><Label>Suburb</Label><SuburbAutocomplete required value={form.suburb} onChange={(value) => setForm((f) => ({ ...f, suburb: value }))} onSelect={(option) => setForm((f) => ({ ...f, suburb: option.suburb, postcode: option.postcode, state: option.state }))} testId="add-suburb" /></div>
              <div className="space-y-1.5"><Label>Postcode</Label><Input value={form.postcode} onChange={setF("postcode")} data-testid="add-postcode" /></div>
              <div className="space-y-1.5 sm:col-span-2"><Label>Practising address <span className="font-normal text-slate-400">(optional)</span></Label><Input value={form.practice_address} onChange={setF("practice_address")} placeholder="Street address or clinic name" data-testid="add-practice-address" /></div>
                    <div className="space-y-1.5">
                      <Label>Gender <span className="font-normal text-slate-400">(optional)</span></Label>
                      <Select value={form.gender} onValueChange={(v) => setForm((f) => ({ ...f, gender: v }))}>
                        <SelectTrigger data-testid="add-gender"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="Female">Female</SelectItem>
                          <SelectItem value="Male">Male</SelectItem>
                          <SelectItem value="Non-binary">Non-binary</SelectItem>
                          <SelectItem value="Prefer not to say">Prefer not to say</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                <Label>Languages <span className="font-normal text-slate-400">(optional)</span></Label>
                <LanguageMultiSelect value={form.languages} onChange={(csv) => setForm((f) => ({ ...f, languages: csv }))} testId="add-languages" />
                <LanguageChips value={form.languages} onRemove={(language) => setForm((f) => ({ ...f, languages: f.languages.split(",").map((item) => item.trim()).filter((item) => item && item !== language).join(", ") }))} />
              </div>
                    <div className="space-y-1.5 sm:col-span-2"><Label>Email</Label><Input type="email" required value={form.email} onChange={setF("email")} placeholder="clinician@clinic.com.au" data-testid="add-email" /></div>
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label>AHPRA registration number</Label>
                      <Input required value={form.ahpra_number}
                        onChange={setF("ahpra_number")}
                        placeholder="Enter the clinician's AHPRA registration number" data-testid="add-ahpra" />
                      <p className="text-xs text-slate-400">Enter the AHPRA registration number shown on the public register. New clinicians are listed for GPs only after SimplyRefer verifies their registration.</p>
                    </div>
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label>Profile photo <span className="font-normal text-slate-400">(optional)</span></Label>
                      <PhotoUpload value={form.photo_url} onChange={(url) => setForm((f) => ({ ...f, photo_url: url }))} testId="add-photo" />
                    </div>
                  </div>
                  <div className="mt-4 flex justify-end gap-2">
                    <Button type="button" variant="ghost" onClick={() => setShowAdd(false)} disabled={adding}>Cancel</Button>
                    <Button type="submit" disabled={adding} data-testid="add-clinician-submit">
                      {adding ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />} Add clinician
                    </Button>
                  </div>
                </form>
              )}

              <div className="mt-6 flex items-center gap-3 text-xs font-medium uppercase tracking-wide text-slate-500" aria-hidden="true">
                <span className="h-px flex-1 bg-slate-200" /> OR <span className="h-px flex-1 bg-slate-200" />
              </div>
              <form onSubmit={inviteExistingClinician} className="mt-4 rounded-md border border-violet-200 bg-violet-50 p-4" data-testid="invite-existing-clinician-form">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                  <div className="min-w-0 flex-1"><Label htmlFor="existing-clinician-email">Invite an existing SimplyRefer clinician</Label><p className="mt-1 text-xs text-slate-600">Their profile, AHPRA status, availability and referral history stay intact. Their individual plan is cancelled only after they accept.</p><Input id="existing-clinician-email" className="mt-2 bg-white" type="email" value={inviteEmail} onChange={(event) => setInviteEmail(event.target.value)} placeholder="clinician@example.com.au" required /></div>
                  <Button type="submit" disabled={invitingExisting || atLimit}>{invitingExisting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Users className="mr-2 h-4 w-4" />} Send consent invitation</Button>
                </div>
                {atLimit && <p className="mt-2 text-xs font-medium text-amber-700">Upgrade the clinic plan before adding another clinician.</p>}
                {membershipInvites.length > 0 && <div className="mt-3 flex flex-wrap gap-2">{membershipInvites.slice(0, 8).map((invite) => <span key={invite.id} className="rounded-full border border-violet-200 bg-white px-3 py-1 text-xs text-slate-700">{invite.clinician_email} · <span className="font-medium">{String(invite.status).toLowerCase()}</span></span>)}</div>}
              </form>

              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {clinicians.map((c) => {
                  const av = availMeta(c.availability_status);
                  return (
                    <div key={c.id} className="rounded-md border border-slate-200 bg-white p-5" data-testid={`clinic-clinician-${c.id}`}>
                      <div className="flex items-center gap-1.5">
                        <h3 className="font-heading text-base font-semibold text-slate-900">{c.first_name} {c.last_name}</h3>
                        {c.is_verified && <BadgeCheck className="h-4 w-4 text-primary" />}
                      </div>
                      <p className="text-sm text-slate-600">{c.profession}</p>
                      <p className="mt-1 text-sm font-medium text-slate-800">{c.specialty}</p>
                      <div className="mt-2 flex items-center gap-1 text-xs text-slate-400"><MapPin className="h-3 w-3" />{c.suburb}{c.state ? ", " + c.state : ""}</div>
                      <div className="mt-3">
                        <Label htmlFor={`clinician-email-${c.id}`}>Availability prompt contact email</Label>
                        <p className="mt-1 text-xs text-slate-500">Used for the passwordless availability prompt when this clinician does not have their own SimplyRefer account.</p>
                        <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                          <Input
                            id={`clinician-email-${c.id}`}
                            type="email"
                            required
                            value={emailDrafts[c.id] ?? c.email ?? ""}
                            onChange={(event) => setEmailDrafts((current) => ({ ...current, [c.id]: event.target.value }))}
                            placeholder="clinician@clinic.com.au"
                            data-testid={`clinician-email-${c.id}`}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            disabled={savingEmail === c.id || (emailDrafts[c.id] ?? c.email ?? "").trim().toLowerCase() === (c.email || "").trim().toLowerCase()}
                            onClick={() => updateClinicianEmail(c)}
                            data-testid={`save-clinician-email-${c.id}`}
                          >
                            {savingEmail === c.id && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Save email
                          </Button>
                        </div>
                      </div>
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <select
                    aria-label={"Availability for " + c.first_name + " " + c.last_name}
                    value={c.availability_status || "TAKING_PATIENTS"}
                    disabled={savingAvailability === c.id || !(c.verification_status === "VERIFIED" || c.is_verified === true)}
                    onChange={(event) => updateClinicianAvailability(c, event.target.value)}
                    title={!(c.verification_status === "VERIFIED" || c.is_verified === true)
                      ? "Availability is locked until AHPRA verification is complete."
                      : "Change clinician availability"}
                    className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
                    data-testid={"clinic-availability-" + c.id}
                  >
                    {Object.entries(AVAIL).map(([value, meta]) => (
                      <option key={value} value={value}>{meta.label}</option>
                    ))}
                  </select>
                        {!c.is_verified && (
                          <span className="inline-flex items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700" data-testid={`pending-verification-${c.id}`}>
                            <Clock className="h-3 w-3" /> Pending AHPRA
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Top referring GP clinics */}
            <div className={activeTab === "insights" ? "" : "hidden"}><TopReferrers rows={insights?.totals?.top_referrers || []} testId="clinic-top-referrers" /></div>

            {/* Optional SimplyRefer References to this account's clinicians */}
            <section className={activeTab === "referrals" ? "" : "hidden"} data-testid="clinic-referrals-section">
              <h2 className="font-heading text-lg font-semibold text-slate-900">SimplyRefer References</h2>
              <p className="mt-1 text-sm text-slate-500">Optional references created by GPs for your clinicians. No patient information.</p>
              {referralThreads.length === 0 ? (
                <div className="mt-4 rounded-md border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="clinic-referrals-empty">
                  <KeyRound className="mx-auto h-7 w-7 text-slate-300" />
                  <p className="mt-2 text-sm text-slate-500">No SimplyRefer References for your clinicians yet.</p>
                </div>
              ) : (
                <div className="mt-4 space-y-3">
                  {referralThreads.map((t) => {
                    const c = t.clinicians;
                    return (
                      <div key={t.id} className="rounded-md border border-slate-200 bg-white p-4" data-testid={`clinic-referral-${t.id}`}>
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <div className="font-medium text-slate-900">{c ? `${c.first_name} ${c.last_name}` : "—"}</div>
                            <div className="mt-0.5 flex items-center gap-1 text-xs text-slate-500"><Building2 className="h-3 w-3" />Referring clinic: <span className="font-medium text-slate-700">{t.gp_clinic_name}</span></div>
                            <div className="mt-0.5 flex items-center gap-1 text-xs text-slate-400"><CalendarClock className="h-3 w-3" />{fmtDateTime(t.created_at)}</div>
                          </div>
                          <span className="inline-flex shrink-0 items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-2 py-1 font-mono text-xs font-semibold text-blue-700">
                            <KeyRound className="h-3 w-3" /> #{t.token}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
